import { useMemo } from "react";
import moment from "moment/moment";
import { IconDotsVertical, IconInfoCircle } from "@tabler/icons-react";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { classnames } from "../../utils";

import Card from "../UI/Card";
import Text from "../UI/Text";
import Title from "../UI/Title";
import { Table } from "../UI/Table";
import Tooltip from "../UI/Tooltip";
import styles from "./BalanceTable.module.css";
import DropdownMenu from "../UI/DropdownMenu.tsx";
import type { Balance } from "../../models/balance";
import { DG_BALANCE_TYPES } from "../../utils/constants/balances";

interface BalanceWithRunning extends Balance {
  runningBalance: number;
}

// Le solde d'en-tête (back, get_tenant_balance_totals) exclut tout ce qui touche au
// dépôt de garantie : le solde cumulé ligne à ligne doit faire pareil pour coïncider avec
// lui. Les retenues sur dépôt restent comptées (appariées à un APPEL_REPARATION négatif
// ou à des loyers impayés).
const countsInSolde = (balance: Balance) =>
  !DG_BALANCE_TYPES.includes(balance.type);

const sumInSolde = (balances: Balance[]) =>
  balances.reduce((sum, b) => (countsInSolde(b) ? sum + b.montant : sum), 0);

interface BalanceTableProps {
  balances: Balance[];
  getBalanceText: (balance: Balance) => string;
  renderRowActions?: (balance: Balance) => React.ReactNode;
  cardPadding?: "xs" | "sm" | "md" | "lg";
}

interface MonthTableProps {
  monthKey: string;
  monthBalances: Balance[];
  startingBalance: number;
  getBalanceText: (balance: Balance) => string;
  renderRowActions?: (balance: Balance) => React.ReactNode;
  isFuture?: boolean;
}

function TVAInfo({ balance }: { balance: Balance }) {
  if (!balance.taux_tva) return null;
  const montantHT = currencyFormatter(Math.abs(balance.montant_ht ?? 0));
  const montantTVA = currencyFormatter(Math.abs(balance.montant_tva ?? 0));
  return (
    <Tooltip content={`${montantHT} HT | TVA : ${montantTVA}`}>
      <span className={styles.tvaTag}>TVA</span>
    </Tooltip>
  );
}

function MonthTable({
  monthKey,
  monthBalances,
  startingBalance,
  getBalanceText,
  renderRowActions,
  isFuture = false,
}: MonthTableProps) {
  // Calculate running balances (transactions are sorted newest first, so process in reverse)
  const balancesWithRunning: BalanceWithRunning[] = [];
  let runningBalance = startingBalance;
  for (let i = monthBalances.length - 1; i >= 0; i--) {
    const balance = monthBalances[i];
    if (countsInSolde(balance)) {
      runningBalance += balance.montant;
    }
    balancesWithRunning.push({ ...balance, runningBalance: runningBalance });
  }
  balancesWithRunning.reverse();

  return (
    <div className={styles.monthSection}>
      <div className={styles.monthHeader}>
        <Title variant="deca" weight="bold" as="h3">
          <Text as="span" className={styles.monthTitleText}>
            {monthKey}
          </Text>{" "}
          {isFuture && (
            <Tooltip content="Ces opérations ne sont pas encore prises en compte dans le solde du locataire. Le solde affiché sur chaque ligne est une projection.">
              <IconInfoCircle size={16} />
            </Tooltip>
          )}
        </Title>
        {!isFuture && (
          <div className={styles.monthBalance}>
            <Text variant="deci" as="span">
              Solde
            </Text>{" "}
            <Text weight="bold" as="span">
              {currencyFormatter(balancesWithRunning[0]?.runningBalance ?? 0)}
            </Text>
          </div>
        )}
      </div>
      <Card padding="xs" className={styles.tableCard}>
        <Table>
          <Table.Body>
            {balancesWithRunning.map((balance, idx) => {
              const isFuture = moment(balance.date_transac).isAfter(
                moment(),
                "day",
              );

              return (
                <Table.Row
                  key={idx}
                  className={classnames(isFuture && styles.futureRow)}
                >
                  {/* Colonne 1: Catégorie + Date */}
                  <Table.Cell>
                    <div className={styles.categoryCell}>
                      <Text weight="bold">{getBalanceText(balance)}</Text>
                      {(balance.comment || balance.description) && (
                        <Tooltip
                          content={balance.comment || balance.description}
                          direction="bottom"
                        >
                          <IconInfoCircle size={14} />
                        </Tooltip>
                      )}
                    </div>
                    <Text variant="deci">
                      {moment(balance.date_transac).format("DD MMM")}
                    </Text>
                  </Table.Cell>

                  {/* Colonne 2: Montant */}
                  <Table.Cell
                    align="right"
                    className={styles.amountCellWrapper}
                  >
                    <div className={styles.amountCell}>
                      <TVAInfo balance={balance} />
                      {balance.montant > 0 ? (
                        <Text
                          weight="bold"
                          as="span"
                          color="success"
                          background
                          className={styles.amountTagCredit}
                        >
                          {currencyFormatter(balance.montant, {
                            withSign: true,
                          })}
                        </Text>
                      ) : (
                        <Text
                          as="span"
                          background
                          className={styles.amountTagCredit}
                        >
                          {currencyFormatter(balance.montant, {
                            withSign: true,
                          })}
                        </Text>
                      )}
                    </div>
                  </Table.Cell>

                  {/* Colonne 3: Solde */}
                  <Table.Cell align="right" className={styles.soldeCellWrapper}>
                    {countsInSolde(balance) ? (
                      <>
                        <Text variant="deci" as="span">
                          Solde
                        </Text>{" "}
                        <Text weight="bold" as="span">
                          {currencyFormatter(balance.runningBalance)}
                        </Text>
                      </>
                    ) : (
                      <Tooltip content="Le dépôt de garantie n'est pas compté dans le solde du compte">
                        <Text variant="deci" as="span">
                          Hors solde
                        </Text>
                      </Tooltip>
                    )}
                  </Table.Cell>

                  {/* Colonne 4: Actions */}
                  <Table.Cell align="right">
                    {renderRowActions && (
                      <DropdownMenu icon={IconDotsVertical} label={"Actions"}>
                        {renderRowActions(balance)}
                      </DropdownMenu>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Card>
    </div>
  );
}

const futureKey = "À venir";

export default function BalanceTable({
  balances,
  getBalanceText,
  renderRowActions,
}: BalanceTableProps) {
  // Group balances by month
  const balancesByMonth = useMemo(() => {
    const grouped: Record<string, Balance[]> = {};

    balances.forEach((balance) => {
      const date = moment(balance.date_transac);
      const isFuture = date.isAfter(moment(), "day");
      const key = isFuture ? futureKey : date.format("MMMM YYYY");

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(balance);
    });

    return grouped;
  }, [balances]);

  // Calculate entries with starting balances
  const entriesWithBalance = useMemo(() => {
    const sortedEntries = Object.entries(balancesByMonth).sort(
      ([, balancesA], [, balancesB]) => {
        return moment(balancesA[0].date_transac).diff(
          moment(balancesB[0].date_transac),
        );
      },
    );

    return sortedEntries
      .reduce<{ key: string; balances: Balance[]; startingBalance: number }[]>(
        (acc, [key, balances]) => {
          if (key == futureKey) {
            return acc;
          }
          const prev = acc[acc.length - 1];
          const startingBalance = prev
            ? prev.startingBalance + sumInSolde(prev.balances)
            : 0;
          acc.push({ key, balances: balances as Balance[], startingBalance });
          return acc;
        },
        [],
      )
      .reverse();
  }, [balancesByMonth]);

  // entriesWithBalance est trié du plus récent au plus ancien : le solde
  // courant est celui atteint à la fin du premier mois de la liste.
  const currentBalance = useMemo(() => {
    const latest = entriesWithBalance[0];
    if (!latest) return 0;
    return latest.startingBalance + sumInSolde(latest.balances);
  }, [entriesWithBalance]);

  return (
    <div className={styles.tablesSection}>
      {balancesByMonth[futureKey] && (
        <MonthTable
          monthKey={futureKey}
          monthBalances={balancesByMonth[futureKey]}
          startingBalance={currentBalance}
          getBalanceText={getBalanceText}
          renderRowActions={renderRowActions}
          isFuture
        />
      )}
      {entriesWithBalance.map(({ key, balances, startingBalance }) => (
        <MonthTable
          key={key}
          monthKey={key}
          monthBalances={balances}
          startingBalance={startingBalance}
          getBalanceText={getBalanceText}
          renderRowActions={renderRowActions}
        />
      ))}
    </div>
  );
}
